import { useMemo, useState } from "react";
import {
  TbAdjustments,
  TbChartPie,
  TbCurrencyReal,
  TbInfoCircle,
  TbMoon,
  TbSun,
} from "react-icons/tb";
import { StablesLimitsSkeleton } from "../../../../Components/Molecules/DashboardSkeleton";
import { PageHeaderWithBack } from "../../../../Components/Molecules";
import { SummaryCard } from "../../../../Components/Atoms/SummaryCard";
import { formatCurrency } from "../../../../Utils/formatters";
import { usePixLimits } from "../../../../Hooks/usePixLimits";
import {
  PIX_LIMIT_PERIODS,
  getLimitTone,
  resolveLimitAvailableCents,
  resolveLimitProgress,
} from "./constants";

const LimitPeriodToggle = ({ period, onChange, periodMeta }) => (
  <div className="mb-4 flex w-full flex-col gap-2 sm:flex-row">
    {Object.values(PIX_LIMIT_PERIODS).map((item) => {
      const isActive = period === item.id;
      const Icon = item.id === "diurno" ? TbSun : TbMoon;
      const hours =
        periodMeta?.[item.id]?.hours || item.hours;

      return (
        <button
          key={item.id}
          type="button"
          onClick={() => onChange(item.id)}
          className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition ${
            isActive
              ? "border-[#e7a73f] bg-brand-cream/60 text-brand-dark"
              : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-brand-dark"
          }`}
        >
          <Icon className="h-4 w-4" />
          <span>{item.label}</span>
          <span className="text-xs font-normal text-gray-400">({hours})</span>
        </button>
      );
    })}
  </div>
);

const LimitCategoryCard = ({ category }) => {
  const Icon = category.icon;
  const availableCents = resolveLimitAvailableCents(category);
  const progress = resolveLimitProgress(category.usedCents, category.totalCents);
  const tone = getLimitTone(progress.isLessThanOne ? 0 : progress.barPercent);

  return (
    <div className="summary-card min-w-0 w-full">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="summary-card-icon">
            <Icon className="h-4 w-4 text-[#e7a73f]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-brand-dark">{category.label}</p>
            <p className="text-xs text-gray-500">{category.description}</p>
          </div>
        </div>
        {category.comingSoon && (
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">
            Em breve
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div>
          <p className="text-[11px] text-gray-400">Utilizado</p>
          <p className="text-sm font-bold text-brand-dark">
            {formatCurrency(category.usedCents)}
          </p>
        </div>
        <div>
          <p className="text-[11px] text-gray-400">Disponível</p>
          <p className="text-sm font-bold text-emerald-600">
            {formatCurrency(availableCents)}
          </p>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <p className="text-[11px] text-gray-400">Limite total</p>
          <p className="text-sm font-bold text-brand-dark">
            {formatCurrency(category.totalCents)}
          </p>
        </div>
      </div>

      <div className="cartoes-progress mt-4">
        <div className="cartoes-progress__track">
          <div
            className={`cartoes-progress__fill cartoes-progress__fill--${tone}`}
            style={{
              width: `${
                category.usedCents > 0
                  ? Math.max(progress.barPercent, 1)
                  : 0
              }%`,
              ...(category.usedCents > 0 ? { minWidth: "4px" } : null),
            }}
          />
        </div>
        <p className="cartoes-progress__label">
          {progress.isLessThanOne
            ? "menos de 1% do limite utilizado neste período"
            : `${progress.labelPercent}% do limite utilizado neste período`}
        </p>
      </div>
    </div>
  );
};

export const PixLimits = () => {
  const [period, setPeriod] = useState("diurno");
  const { byPeriod, periodMetaFor, loading, error, reload } = usePixLimits();
  const categories = byPeriod[period] || [];
  const periodMeta = periodMetaFor(period);

  const summary = useMemo(() => {
    const totals = categories.reduce(
      (acc, item) => ({
        usedCents: acc.usedCents + item.usedCents,
        totalCents: acc.totalCents + item.totalCents,
        availableCents: acc.availableCents + resolveLimitAvailableCents(item),
      }),
      { usedCents: 0, totalCents: 0, availableCents: 0 },
    );

    return {
      ...totals,
      ...resolveLimitProgress(totals.usedCents, totals.totalCents),
    };
  }, [categories]);

  const toggleMeta = useMemo(
    () => ({
      diurno: periodMetaFor("diurno"),
      noturno: periodMetaFor("noturno"),
    }),
    [periodMetaFor],
  );

  return (
    <div className="w-full">
      <PageHeaderWithBack
        title="Limites transacionais Pix"
        subTitle="Consulte seus limites de envio e recebimento por período, conforme regras do Banco Central."
        backTo="/pix"
      />

      <LimitPeriodToggle
        period={period}
        onChange={setPeriod}
        periodMeta={toggleMeta}
      />

      {loading ? (
        <StablesLimitsSkeleton />
      ) : error ? (
        <div className="app-card app-empty-state">
          <p>{error}</p>
          <button
            type="button"
            className="btn-outline mt-2 !rounded-full !px-4"
            onClick={reload}
          >
            Tentar novamente
          </button>
        </div>
      ) : categories.length === 0 ? (
        <div className="app-card app-empty-state">
          <p>Nenhum limite Pix configurado para esta conta.</p>
          <button
            type="button"
            className="btn-outline mt-2 !rounded-full !px-4"
            onClick={reload}
          >
            Atualizar
          </button>
        </div>
      ) : (
        <>
          <div className="dashboard-card-grid dashboard-card-grid-pix mb-5">
            <SummaryCard
              icon={TbAdjustments}
              title={`Limite total (${periodMeta.label.toLowerCase()})`}
              value={formatCurrency(summary.totalCents)}
              footer={
                <p className="mt-2 text-xs text-gray-500">
                  Período {periodMeta.hours}
                </p>
              }
            />
            <SummaryCard
              icon={TbChartPie}
              title="Utilizado"
              value={formatCurrency(summary.usedCents)}
              footer={
                <p className="mt-2 text-xs text-gray-500">
                  {summary.isLessThanOne
                    ? "menos de 1% do limite consumido"
                    : `${summary.labelPercent}% do limite consumido`}
                </p>
              }
            />
            <SummaryCard
              icon={TbCurrencyReal}
              title="Disponível"
              value={formatCurrency(summary.availableCents)}
              footer={
                <p className="mt-2 text-xs text-gray-500">
                  Saldo restante para transações
                </p>
              }
            />
          </div>

          <div className="app-card overflow-hidden">
            <div className="border-b border-gray-100 px-4 py-3">
              <p className="text-sm font-bold text-brand-dark">
                Limites por categoria — {periodMeta.label}
              </p>
              <p className="text-xs text-gray-500">{periodMeta.description}</p>
            </div>

            <div className="grid gap-3 p-4 md:grid-cols-2">
              {categories.map((category) => (
                <LimitCategoryCard
                  key={`${category.id}-${category.period}-${category.periodHourStart}`}
                  category={category}
                />
              ))}
            </div>
          </div>
        </>
      )}

      <div className="mt-4 flex gap-3 rounded-xl border border-[#e7a73f]/20 bg-brand-cream/40 px-4 py-3">
        <TbInfoCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#e7a73f]" />
        <div className="text-xs leading-relaxed text-gray-600">
          <p className="font-semibold text-brand-dark">Sobre os limites Pix</p>
          <p className="mt-1">
            Os limites transacionais são definidos pelo Banco Central do Brasil (Bacen) e
            podem variar conforme o horário (diurno ou noturno), o tipo de transação e o
            perfil da conta. Os valores exibidos são referentes ao período atual e são
            renovados automaticamente a cada ciclo.
          </p>
          <p className="mt-2">
            Para solicitar alteração de limites, entre em contato com o suporte da sua
            instituição financeira.
          </p>
        </div>
      </div>
    </div>
  );
};
